import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import Form from 'components/form/Form';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import useBillingDetailsValidation from 'pages/Settings/Payment/hooks/useBillingValidation';

import { COMPANY, OTO_LINK } from 'services/consts';

import { setUserGeneralSettings } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { ACCOUNT_TYPES } from 'utils/constants';

import { faFloppyDisk } from '@fortawesome/pro-light-svg-icons';

import AccountType from '../OTOLinkOnboarding/Tabs/AccountType';
import { getDeliveryCompanies, getOTOLinkSettings } from './OtoLinkSettingsSlice';
import PaymentMethodTab from './PaymentMethodTab';
import ShippingMethodTab from './ShippingMethodTab';

const OtoLinkSettings = NiceModal.create(() => {
	const axios = useAxiosPrivate();
	const modal = useModal();
	const isAuthorized = useAccessControl();
	const { t } = useTranslation();
	const { billingDefaults } = useBillingDetailsValidation();

	const user = useSelector((state) => state.user);
	const { settings: linkSettings, deliveryCompanies } = useSelector((state) => state.otoLinkSettings);
	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();

	const schema = yup.object({
		allowedDCIds: yup.array().min(1).required(),
		paymentMethod: yup.array().min(1).required(),
		pickupType: yup.array().required()
	});

	const accountTypeSchema = useMemo(
		() =>
			yup.object().shape({
				invoiceType: yup
					.string()
					.nullable()
					.oneOf([ACCOUNT_TYPES.PERSONAL, ACCOUNT_TYPES.COMPANY, ACCOUNT_TYPES.FREELANCER])
					.required(),
				notVATRegistered: yup.boolean(),
				CRNumber: yup
					.string()
					.nullable()
					.when('invoiceType', ([invoiceType], schema) => {
						if (
							invoiceType === ACCOUNT_TYPES.COMPANY &&
							isAuthorized('/company-settings/CommonInvoiceType')
						) {
							return schema.checkCRNumber().required();
						}
						return schema;
					}),
				freelanceDocument: yup
					.mixed()
					.nullable()
					.when('invoiceType', ([invoiceType], schema) =>
						invoiceType === ACCOUNT_TYPES.FREELANCER ? schema.required() : schema
					),
				civilID: yup
					.mixed()
					.nullable()
					.when('invoiceType', ([invoiceType], schema) => {
						if (
							invoiceType === ACCOUNT_TYPES.FREELANCER &&
							isAuthorized('/company-settings/CommonInvoiceType')
						)
							return schema.required();
						if (invoiceType === ACCOUNT_TYPES.COMPANY && isAuthorized('/company-settings/KSAInvoiceType'))
							return schema.required();
						return schema;
					}),
				CRDocumentURL: yup
					.mixed()
					.nullable()
					.when('invoiceType', ([invoiceType], schema) =>
						invoiceType === ACCOUNT_TYPES.COMPANY ? schema.required() : schema
					),
				billingInfo: yup
					.object()
					.shape({
						text: yup.string().nullable().required(),
						countryName: yup.string().nullable().required(),
						stateName: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) => schema.required()
							}),
						cityName: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) => schema.required()
							}),
						districtName: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) => schema.required()
							}),
						buildingName: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) =>
									isAuthorized('/company-settings/KSAInvoiceType')
										? schema
												.min(
													4,
													t('validation.numberFieldLength', {
														length: 4,
														fieldName: t('buildingNo')
													})
												)
												.max(
													4,
													t('validation.numberFieldLength', {
														length: 4,
														fieldName: t('buildingNo')
													})
												)
												.matches(/\d/, t('validation.numberFieldPositive'))
												.required()
										: schema.required()
							}),
						street: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) => schema.required()
							}),
						zipCode: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) =>
									isAuthorized('/company-settings/KSAInvoiceType')
										? schema
												.min(
													5,
													t('validation.numberFieldLength', {
														length: 5,
														fieldName: t('zipCode')
													})
												)
												.max(
													5,
													t('validation.numberFieldLength', {
														length: 5,
														fieldName: t('zipCode')
													})
												)
												.matches(/\d/, t('validation.numberFieldPositive'))
												.requiredZipCode()
										: schema.requiredZipCode()
							})
					})
					.when('invoiceType', ([invoiceType], schema) => {
						// this form only requires billing info for the company
						if (invoiceType === ACCOUNT_TYPES.COMPANY) {
							return schema;
						}
						return yup.object();
					})
					.nullable()
			}),
		[]
	);

	const fillForm = () => {
		const generalSettings = user?.generalSettings || {};
		const defaultValues = {
			allowedDCIds: [],
			paymentMethod: ['COD'],
			pickupType: ['branchDropOff', 'pickupByDc'],
			invoiceType: generalSettings.invoiceType,
			VATNumber: generalSettings.VATNumber,
			CRNumber: generalSettings.CRNumber,
			companyName: generalSettings.companyName,
			taxOffice: generalSettings.taxOffice,
			notVATRegistered: generalSettings.notVATRegistered,
			freelanceDocument: generalSettings.freelanceDocument,
			CRDocumentURL: generalSettings.CRDocumentURL,
			civilID: generalSettings.civilID,
			fullName: generalSettings.fullName,
			idNumber: generalSettings.idNumber,
			billingInfo: billingDefaults.address
		};
		if (!linkSettings.linkSettingsId) {
			return defaultValues;
		}
		return {
			...defaultValues,
			paymentMethod: linkSettings.paymentMethod === 'BOTH' ? ['CC', 'COD'] : [linkSettings.paymentMethod],
			allowedDCIds: linkSettings.allowedDCs,
			pickupType:
				linkSettings?.pickupOptions === 'BOTH' ? ['branchDropOff', 'pickupByDc'] : [linkSettings.pickupOptions]
		};
	};

	const formMethods = useForm({
		resolver: yupResolver(schema.concat(accountTypeSchema)),
		defaultValues: fillForm()
	});

	const {
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading, dirtyFields },
		handleSubmit,
		watch
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const updateAccountType = async (data) => {
		try {
			const payload = {
				VATNumber: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.VATNumber : null,
				CRNumber: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.CRNumber : null,
				fullName: data.invoiceType === ACCOUNT_TYPES.PERSONAL ? data.fullName : null,
				idNumber: data.invoiceType === ACCOUNT_TYPES.PERSONAL ? data.idNumber : null,
				taxOffice: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.taxOffice : null,
				notVATRegistered: data.notVATRegistered,
				companyName: data.companyName,
				CRDocumentURL:
					data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.CRDocumentURL?.path || data?.CRDocumentURL : null,
				freelanceDocument:
					data.invoiceType === ACCOUNT_TYPES.FREELANCER
						? data.freelanceDocument?.path || data?.freelanceDocument
						: null,
				civilID:
					data.invoiceType === ACCOUNT_TYPES.FREELANCER || data.invoiceType === ACCOUNT_TYPES.COMPANY
						? data.civilID?.path || data?.civilID
						: null,
				invoiceType: data.invoiceType,
				address: data.billingInfo
			};
			const response = await axios.post(COMPANY.UPDATE_GENERAL_SETTINGS, {
				ID: user?.generalSettings?.id,
				address: {
					text: user?.generalSettings?.address?.text
				},
				...payload
			});
			if (response.status === 200) {
				dispatch(setUserGeneralSettings(payload));
			} else {
				throw new Error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			throw error;
		}
	};

	const onSubmit = useCallback(
		async (values) => {
			try {
				const endpoint = linkSettings.linkSettingsId
					? OTO_LINK.UPDATE_OTO_LINK_SETTINGS
					: OTO_LINK.ADD_OTO_LINK_SETTINGS;

				const response = await axios.post(endpoint, {
					linkSettingsId: linkSettings?.linkSettingsId,
					allowedDCIds: values.allowedDCIds.filter((id) => deliveryCompanies.some((dc) => dc.ID === id)),
					paymentMethod: values.paymentMethod.length > 1 ? 'BOTH' : values.paymentMethod[0],
					pickupOptions: values.pickupType.length > 1 ? 'BOTH' : values.pickupType[0]
				});

				const hasAccountTypeChange = Object.keys(dirtyFields).some((field) =>
					Object.keys(accountTypeSchema.fields).includes(field)
				);

				if (hasAccountTypeChange) {
					await updateAccountType(values);
				}

				if (response.status === 200) {
					modal.remove();
					showSuccess();
					dispatch(getOTOLinkSettings());
				} else {
					showError();
					AppLogger.log(response);
				}
			} catch (error) {
				AppLogger.log(error);
				showError();
			}
		},
		[linkSettings, deliveryCompanies, dirtyFields]
	);

	const tabs = [
		{
			label: 'paymentMethod',
			component: PaymentMethodTab
		},
		{
			label: 'shippingMethod',
			component: ShippingMethodTab
		},
		{
			label: 'accountType',
			component: AccountType
		}
	];

	const isLoading = isFormLoading || isSubmitting;

	const watchPaymentMethod = watch('paymentMethod');
	const watchPickupType = watch('pickupType');

	useEffect(() => {
		if (watchPaymentMethod?.length && watchPickupType) {
			dispatch(
				getDeliveryCompanies({
					paymentMethod: watchPaymentMethod?.length > 1 ? 'BOTH' : watchPaymentMethod?.[0],
					pickupType: watchPickupType?.length > 1 ? 'BOTH' : watchPickupType?.[0]
				})
			);
		}
	}, [watchPaymentMethod, watchPickupType]);

	return (
		<Modal fullwidth maxWidth="x-large" onClose={modal.remove}>
			<ModalTitle>
				<Typography variant="p1">otoLinkSettings</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<Tabs data={tabs} />
				</Form>
			</ModalContent>

			<ModalActions>
				<Button
					label="cancel"
					variant="blackOutline"
					onClick={modal.remove}
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
				/>
				<Button
					loading={isLoading}
					className="saveButton"
					onClick={handleSubmit(onSubmit, AppLogger.log)}
					icon={faFloppyDisk}
					// disabled={!isValid || !isDirty}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="save"
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default OtoLinkSettings;
